import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form"
import { usePayment } from "../use-payment"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { useGetLoanProviders } from "@/hooks/use-loan"
import Section from "../section"
// import AvanseFormFields from "./avanse"
// import FibeFormFields from "./fibe"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Check, Loader2 } from "lucide-react"
import VerifyOTP from "../../components/verify-otp"
import {
	employmentTypeSchema,
	maritalStatusSchema,
	fibeRelationSchema,
	avanseRelationSchema,
	propelldRelationSchema,
} from "../schemas"
import { useEffect, useMemo, useState } from "react"
import { useFormStore } from "@/stores/use-form-store"
import { useShallow } from "zustand/react/shallow"
import {
	PaymentStatus,
	type CheckPaymentStatusResponse,
	type LOAN_PROVIDER,
} from "@/types/form-types"
import { format } from "date-fns"
import { getStudentInfo } from "@/lib/utils"
import type { UseFormReturn } from "react-hook-form"
import { logger } from "@/utils/logger"

const LoanProviderForm = ({
	form,
	paymentStatus,
	isLoadingLoanDetails,
}: {
	form: UseFormReturn
	paymentStatus?: CheckPaymentStatusResponse
	isLoadingLoanDetails: boolean
}) => {
	const {
		loanProviderForm,
		course_id,
		setValues,
		getValues,
		feeSummary,
		selectedPaymentPlan,
	} = usePayment()
	const { payment_status } = getValues()
	const { config: formConfig, applicationSubmission } = useFormStore(
		useShallow(state => ({
			applicationId: state.applicationId,
			config: state.config,
			applicationSubmission: state.applicationSubmission,
		})),
	)
	const { data: loanProviders, isLoading: isLoadingLoanProviders } =
		useGetLoanProviders({
			course_id: course_id,
		})
	const { watch } = loanProviderForm.form
	// const selectedLoanProviderId = watch("loan_provider_id")

	// Get Student Info
	const first_name_field = getStudentInfo(formConfig!, "first_name")
	const last_name_field = getStudentInfo(formConfig!, "last_name")
	const dob_field = getStudentInfo(formConfig!, "date_of_birth")

	// Get Frequency from fee summary
	const isYearly = useMemo(() => {
		if (feeSummary?.length > 0) {
			if (selectedPaymentPlan) {
				return (
					feeSummary.find(
						item =>
							item.component_id ===
							selectedPaymentPlan.replace(/^(self|loan)-/, ""),
					)?.frequency === "PER_YEAR"
				)
			}
		}
		return false
	}, [feeSummary, selectedPaymentPlan])

	// reset loan provider tenure when loan provider changes
	const selectedPaymentPlanMemo = useMemo(
		() => selectedPaymentPlan,
		[selectedPaymentPlan],
	)
	useEffect(() => {
		loanProviderForm.form.resetField("loan_provider_tenure")
	}, [selectedPaymentPlanMemo])

	// previous verified phone number
	const [previousVerifiedPhoneNumber, setPreviousVerifiedPhoneNumber] =
		useState<string>("-1")

	// const {
	// 	useCheckPaymentStatus,
	// 	// useHandleDownloadReceipt,
	// } = useHandlePayment(paymentParams)
	// const { data: paymentStatus, isLoading: isLoadingLoanDetails } =
	// 	useCheckPaymentStatus({
	// 		category: FeeCategory.TUITION,
	// 		semester: periodNumber,
	// 	})

	const loanDetails = useMemo(() => {
		return paymentStatus?.loan_details
	}, [paymentStatus])

	useEffect(() => {
		// This condition now ensures BOTH data sources are loaded and available.
		if (
			loanDetails &&
			loanProviders &&
			!isLoadingLoanDetails &&
			!isLoadingLoanProviders
		) {
			setValues({
				order_id: loanDetails?.order_id,
				loan_id: loanDetails?.loan_id,
			})

			setPreviousVerifiedPhoneNumber(
				loanDetails?.applicant?.phone || "-1",
			)

			// The form is now reset only when `loanDetails` AND `loanProviders` are ready.
			loanProviderForm.form.reset({
				loan_provider_id: loanDetails?.lender_id,
				loan_provider_tenure: loanDetails?.tenure_in_months,
				applicant_marital_status:
					loanDetails?.applicant?.additional_details?.applicantDetails
						?.marital_status,
				applicant_employment_type:
					loanDetails?.applicant?.additional_details?.applicantDetails
						?.employment_type,
				applicant_monthly_income: Number(
					loanDetails?.applicant?.additional_details?.applicantDetails
						?.monthly_income,
				),
				applicant_pan_number:
					loanDetails?.applicant?.additional_details?.applicantDetails
						?.pan_number,
				applicant_pincode:
					loanDetails?.applicant?.additional_details?.applicantDetails
						?.pincode,
				// Corrected line
				applicant_dob: format(
					new Date(loanDetails?.applicant?.dob),
					"yyyy-MM-dd",
				),
				applicant_email: loanDetails?.applicant?.email,
				applicant_first_name: loanDetails?.applicant?.first_name,
				applicant_full_name:
					loanDetails?.applicant?.first_name +
					" " +
					loanDetails?.applicant?.last_name,
				applicant_last_name: loanDetails?.applicant?.last_name,
				applicant_phone_number: loanDetails?.applicant?.phone,
				applicant_relation: loanDetails?.applicant?.relationship,
				country_code: loanDetails?.applicant?.country_code,
				otp_verified:
					payment_status ===
						PaymentStatus.LOAN_APPROVED_PENDING_LOAN_DISBURSEMENT ||
					payment_status ===
						PaymentStatus.ORDER_CREATED_PENDING_LOAN_APPROVAL ||
					false,
				student_dob: loanDetails?.applicant?.additional_details
					?.student_info?.dob
					? format(
							new Date(
								loanDetails?.applicant?.additional_details
									?.student_info?.dob,
							),
							"yyyy-MM-dd",
						)
					: undefined,
			})

			loanProviderForm.form.setValue(
				"loan_provider_name",
				(loanProviders?.find(
					provider => provider.lender_id === loanDetails?.lender_id,
				)?.identifier as "FIBE" | "AVANSE" | "PROPELLD" | undefined) ||
					"FIBE",
			)

			setTimeout(() => {
				// Relation
				loanProviderForm.form.setValue(
					"applicant_relation",
					loanDetails?.applicant?.relationship,
				)
				// Marital Status
				loanProviderForm.form.setValue(
					"applicant_marital_status",
					loanDetails?.applicant?.additional_details?.applicantDetails
						?.marital_status,
				)
				// Employment Type
				loanProviderForm.form.setValue(
					"applicant_employment_type",
					loanDetails?.applicant?.additional_details?.applicantDetails
						?.employment_type,
				)
				if (
					loanProviderForm.form.watch("loan_provider_name") === "FIBE"
				) {
					loanProviderForm.form.setValue("otp_verified", true)
					loanProviderForm.form.trigger("otp_verified")
				}
			}, 100)
		}
		// Add ALL relevant dependencies to the array.
	}, [
		loanDetails,
		loanProviders,
		isLoadingLoanDetails,
		isLoadingLoanProviders,
		payment_status,
	])

	const [tenures, setTenures] = useState<number[]>([])

	useEffect(() => {
		if (watch("loan_provider_id")) {
			const tenures = loanProviders
				?.find(
					provider =>
						provider.lender_id === watch("loan_provider_id"),
				)
				?.tenures?.map(tenure => tenure.tenure_in_months)
			setTenures(tenures || [])
			if (loanDetails?.tenure_in_months) {
				loanProviderForm.form.setValue(
					"loan_provider_tenure",
					loanDetails?.tenure_in_months,
				)
			}
		}
	}, [watch("loan_provider_id")])

	useEffect(() => {
		if (
			form.watch(first_name_field?.field_id || "") ||
			applicationSubmission[first_name_field?.field_id || ""]
		) {
			loanProviderForm.form.setValue(
				"student_first_name",
				form.watch(first_name_field?.field_id || "") ||
					applicationSubmission[first_name_field?.field_id || ""],
			)
		} else {
			loanProviderForm.form.setValue(
				"student_first_name",
				loanDetails?.applicant?.first_name || "",
			)
		}
		if (
			form.watch(last_name_field?.field_id || "") ||
			applicationSubmission[last_name_field?.field_id || ""]
		) {
			loanProviderForm.form.setValue(
				"student_last_name",
				form.watch(last_name_field?.field_id || "") ||
					applicationSubmission[last_name_field?.field_id || ""],
			)
		} else {
			loanProviderForm.form.setValue(
				"student_last_name",
				loanDetails?.applicant?.last_name || "",
			)
		}
		if (!loanDetails && !isLoadingLoanDetails) {
			const rawDateValue =
				form.watch(dob_field?.field_id || "") ||
				applicationSubmission[dob_field?.field_id || ""] ||
				null

			loanProviderForm.form.setValue(
				"student_dob",
				rawDateValue
					? format(new Date(rawDateValue), "yyyy-MM-dd")
					: "",
			)
		}
	}, [loanDetails, isLoadingLoanDetails])

	// set otp as false if the phone number is changed
	useEffect(() => {
		const currentPhoneNumber = loanProviderForm.form.watch(
			"applicant_phone_number",
		)
		const verifiedNumber =
			previousVerifiedPhoneNumber === "-1"
				? loanDetails?.applicant?.phone
				: previousVerifiedPhoneNumber

		// Only set to false if the current number is different from the verified number.
		if (currentPhoneNumber !== verifiedNumber) {
			logger.info("Phone number changed, setting OTP verified to false.")
			loanProviderForm.form.setValue("otp_verified", false)
		} else if (loanProviderForm.form.watch("otp_verified") === false) {
			logger.info(
				"Phone number matches verified number, setting OTP verified to true.",
			)
			loanProviderForm.form.setValue("otp_verified", true)
		}

		loanProviderForm.form.trigger("otp_verified")
	}, [
		loanProviderForm.form.watch("applicant_phone_number"),
		loanDetails,
		previousVerifiedPhoneNumber,
	])

	return isLoadingLoanProviders || isLoadingLoanDetails ? (
		<div>
			<Loader2 className="animate-spin" />
		</div>
	) : (
		<div className="space-y-8">
			{/* Loan Provider Details */}
			<Section
				title="Loan Provider Details"
				fields={[
					<FormField
						key={"loan_provider_id"}
						control={loanProviderForm.form.control}
						name="loan_provider_id"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Loan Provider</FormLabel>
								<Select
									value={
										field.value || loanDetails?.lender_id
									}
									disabled={field.disabled}
									onValueChange={value => {
										field.onChange(value)
										//Filter out the selected lender
										loanProviderForm.form.setValue(
											"loan_provider_name",
											(loanProviders?.find(
												provider =>
													provider.lender_id ===
													value,
											)?.identifier as
												| "FIBE"
												| "AVANSE"
												| "PROPELLD") || "FIBE",
										)
										loanProviderForm.form.trigger(
											"otp_verified",
										)
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select Loan Provider" />
									</SelectTrigger>
									<SelectContent>
										{loanProviders &&
											loanProviders?.map(
												(provider: LOAN_PROVIDER) => (
													<SelectItem
														key={provider.lender_id}
														value={
															provider.lender_id
														}
													>
														{provider.display_name}
													</SelectItem>
												),
											)}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>,
					<FormField
						key={"loan_tenure"}
						control={loanProviderForm.form.control}
						name="loan_provider_tenure"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Loan Tenure</FormLabel>
								<Select
									value={
										field.value?.toString() ||
										loanDetails?.tenure_in_months?.toString()
									}
									disabled={
										field.disabled ||
										!watch("loan_provider_name")
									}
									onValueChange={value => {
										field.onChange(Number(value))
									}}
								>
									<SelectTrigger>
										{watch("loan_provider_name") ===
										undefined ? (
											<SelectValue placeholder="Select Loan Provider First" />
										) : (
											<SelectValue placeholder="Select Tenure" />
										)}
									</SelectTrigger>
									<SelectContent>
										{watch("loan_provider_id") &&
											loanProviders &&
											tenures
												?.filter(tenure => {
													// remove the tenure which is above 12 months if isYearly is true
													if (isYearly) {
														return tenure <= 12
													}
													return true
												})
												?.map((tenure, index) => {
													return (
														<SelectItem
															key={index}
															value={tenure?.toString()}
														>
															{tenure}
														</SelectItem>
													)
												})}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>,
				]}
			/>
			{/* Student Details */}
			<Section
				title="Student Details"
				fields={[
					<FormField
						// First Name
						key={"student_first_name"}
						control={loanProviderForm.form.control}
						name="student_first_name"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									First Name{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Input
										disabled={
											form.watch(
												first_name_field?.field_id ||
													"",
											) ||
											applicationSubmission[
												first_name_field?.field_id || ""
											]
										}
										{...field}
										placeholder="e.g., John"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,
					<FormField
						// Last Name
						key={"student_last_name"}
						control={loanProviderForm.form.control}
						name="student_last_name"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Last Name{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Input
										disabled={
											form.watch(
												last_name_field?.field_id || "",
											) ||
											applicationSubmission[
												last_name_field?.field_id || ""
											]
										}
										{...field}
										placeholder="e.g., Doe"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,
					<FormField
						// Date of Birth
						key={"student_dob"}
						control={loanProviderForm.form.control}
						name="student_dob"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Date of Birth{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Input
										type="date"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,
				]}
			/>
			{/* Applicant Details */}
			<Section
				title="Loan Applicant Details"
				fields={[
					<FormField
						// First Name
						key={"applicant_first_name"}
						control={loanProviderForm.form.control}
						name="applicant_first_name"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									First Name{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Input
										{...field}
										placeholder="e.g., John"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,
					<FormField
						// Last Name
						key={"applicant_last_name"}
						control={loanProviderForm.form.control}
						name="applicant_last_name"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Last Name{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Input
										{...field}
										placeholder="e.g., Doe"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,
					<FormField
						// Full Name
						key={"applicant_full_name"}
						control={loanProviderForm.form.control}
						name="applicant_full_name"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Full Name{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Input
										{...field}
										placeholder="e.g., John Doe"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,
					<FormField
						// Email
						key={"applicant_email"}
						control={loanProviderForm.form.control}
						name="applicant_email"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Email{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Input
										{...field}
										placeholder="e.g., johndoe@example.com"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,
					<FormField
						// OTP Verification
						key={"otp_verified"}
						control={loanProviderForm.form.control}
						name="otp_verified"
						render={() => (
							<FormItem>
								<FormLabel>
									{loanProviderForm.form.watch(
										"loan_provider_name",
									) === "FIBE" && "Verify "}
									Applicant's Phone Number
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<FormField
										control={loanProviderForm.form.control}
										name="applicant_phone_number"
										render={({ field }) => {
											return (
												<FormItem>
													<FormControl>
														<div className="flex items-center gap-2">
															<Input
																type="number"
																placeholder="Applicant's phone number"
																onWheel={e =>
																	e.currentTarget.blur()
																}
																{...field}
															/>
															{loanProviderForm.form.watch(
																"otp_verified",
															) &&
															loanProviderForm.form.watch(
																"loan_provider_name",
															) === "FIBE" ? (
																<Badge
																	variant={
																		"secondary"
																	}
																	className="bg-green-500"
																>
																	<Check className="h-4 w-4" />{" "}
																	Verified
																</Badge>
															) : (
																loanProviderForm.form.watch(
																	"loan_provider_name",
																) === "FIBE" &&
																loanProviderForm.form.watch(
																	"otp_verified",
																) === false && (
																	<VerifyOTP
																		mobileNumber={
																			loanProviderForm.form.watch(
																				"applicant_phone_number",
																			) ||
																			""
																		}
																		setOtpVerified={otpVerified => {
																			loanProviderForm.form.setValue(
																				"otp_verified",
																				otpVerified,
																			)
																			if (
																				otpVerified
																			) {
																				setPreviousVerifiedPhoneNumber(
																					loanProviderForm.form.getValues(
																						"applicant_phone_number",
																					) ||
																						"",
																				)
																			}
																			loanProviderForm.form.trigger(
																				"otp_verified",
																			)
																		}}
																	/>
																)
															)}
														</div>
													</FormControl>
													<FormMessage />
												</FormItem>
											)
										}}
									/>
								</FormControl>
								{loanProviderForm.form.watch(
									"loan_provider_name",
								) === "FIBE" &&
									!loanProviderForm.form.watch(
										"otp_verified",
									) && (
										<FormDescription>
											Please verify applicant mobile
											number.
										</FormDescription>
									)}
								<FormMessage />
							</FormItem>
						)}
					/>,

					<FormField
						// Date of Birth
						key={"applicant_dob"}
						control={loanProviderForm.form.control}
						name="applicant_dob"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Date of Birth{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Input
										type="date"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,

					<FormField
						// Relation
						key={"applicant_relation"}
						control={loanProviderForm.form.control}
						name="applicant_relation"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Relation{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Select
										value={field.value}
										onValueChange={field.onChange}
										disabled={
											field.disabled ||
											!loanProviderForm.form.watch(
												"loan_provider_name",
											)
										}
									>
										<SelectTrigger>
											{loanProviderForm.form.watch(
												"loan_provider_name",
											) === undefined ? (
												<SelectValue placeholder="Select Loan Provider First" />
											) : (
												<SelectValue placeholder="Select Relationship" />
											)}
										</SelectTrigger>
										<SelectContent>
											{loanProviderForm.form.watch(
												"loan_provider_name",
											) === "FIBE"
												? fibeRelationSchema.options.map(
														(
															status: string,
															index: number,
														) => (
															<SelectItem
																key={index}
																value={status}
															>
																{status}
															</SelectItem>
														),
													)
												: loanProviderForm.form.watch(
															"loan_provider_name",
													  ) === "AVANSE"
													? avanseRelationSchema.options.map(
															(
																status: string,
																index: number,
															) => (
																<SelectItem
																	key={index}
																	value={
																		status
																	}
																>
																	{status}
																</SelectItem>
															),
														)
													: loanProviderForm.form.watch(
																"loan_provider_name",
														  ) === "PROPELLD"
														? propelldRelationSchema.options.map(
																(
																	status: string,
																	index: number,
																) => (
																	<SelectItem
																		key={
																			index
																		}
																		value={
																			status
																		}
																	>
																		{status}
																	</SelectItem>
																),
															)
														: null}
										</SelectContent>
									</Select>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,
					<FormField
						// Pan Number
						key={"applicant_pan_number"}
						control={loanProviderForm.form.control}
						name="applicant_pan_number"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Pan Number{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Input
										{...field}
										placeholder="e.g., AP12345678"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,

					<FormField
						// 	Marital Status
						key={"applicant_marital_status"}
						control={loanProviderForm.form.control}
						name="applicant_marital_status"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Marital Status{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Select
										disabled={field.disabled}
										value={field.value}
										onValueChange={field.onChange}
									>
										<SelectTrigger>
											<SelectValue placeholder="Select marital status" />
										</SelectTrigger>
										<SelectContent>
											{maritalStatusSchema.options.map(
												(
													status: string,
													index: number,
												) => (
													<SelectItem
														key={index}
														value={status}
													>
														{status}
													</SelectItem>
												),
											)}
										</SelectContent>
									</Select>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,

					<FormField
						// Pincode
						key={"applicant_pincode"}
						control={loanProviderForm.form.control}
						name="applicant_pincode"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Pincode{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Input
										{...field}
										placeholder="e.g., 123456"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,

					<FormField
						// Employment Type
						key={"applicant_employment_type"}
						control={loanProviderForm.form.control}
						name="applicant_employment_type"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Employment Type{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Select
										value={field.value}
										onValueChange={field.onChange}
										disabled={field.disabled}
									>
										<SelectTrigger>
											<SelectValue placeholder="Select employment type" />
										</SelectTrigger>
										<SelectContent>
											{employmentTypeSchema.options.map(
												(type, index) => (
													<SelectItem
														key={index}
														value={type}
													>
														{type}
													</SelectItem>
												),
											)}
										</SelectContent>
									</Select>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,

					<FormField
						// Monthly Income
						key={"applicant_monthly_income"}
						control={loanProviderForm.form.control}
						name="applicant_monthly_income"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									Monthly Income{" "}
									<span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Input
										type="number"
										placeholder="e.g., 123456"
										{...field}
										onWheel={e => e.currentTarget.blur()}
										onChange={e =>
											field.onChange(
												e.target.value === undefined ||
													e.target.value === ""
													? undefined
													: parseFloat(
															e.target.value,
														),
											)
										}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>,
				]}
			/>
			{/* Loan Provider based Required Fields */}
			{/* {loanProviders?.find(
				provider =>
					provider.lender_id ===
					loanProviderForm.form.watch("loan_provider_id"),
			)?.identifier === "AVANSE" && <AvanseFormFields />}
			{loanProviders?.find(
				provider =>
					provider.lender_id ===
					loanProviderForm.form.watch("loan_provider_id"),
			)?.identifier === "FIBE" && <FibeFormFields />} */}
		</div>
	)
}

export default LoanProviderForm
